import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate } from '../lib/api.js';
import { StudioMark } from '../lib/branding.jsx';
import { IconArrowRight } from '../lib/icons.jsx';
import { useGallery } from './GalleryLayout.jsx';
import { WatchOnTvSheet } from './WatchOnTv.jsx';

/**
 * The cover. Full-screen hero, then two entrances — the Film and the
 * Photographs — each a large image-backed door into its own page. This is the
 * moment that sets the tone, so it stays deliberately sparse: the couple's name,
 * the date, and two ways in. Nothing scrolls forever here.
 */
export default function GalleryHome() {
  const { slug, gallery, assets, photos, videos, branding } = useGallery();
  const navigate = useNavigate();
  const [tvOpen, setTvOpen] = useState(false);

  const cover = assets.find((a) => a.id === gallery.cover_asset_id) || photos[0] || videos[0];
  const filmPoster = videos[0]?.poster || videos[0]?.thumb || photos[Math.floor(photos.length / 2)]?.thumb;
  const photoTile = (photos[0]?.id === cover?.id ? photos[1] : photos[0])?.thumb || photos[0]?.thumb;

  const [heroLoaded, setHeroLoaded] = useState(false);
  const [videoBg, setVideoBg] = useState(false);

  // The hero plays the first film muted behind the name — but only where it's
  // wanted: on a real screen (not phones, to spare mobile data), when the visitor
  // hasn't asked for reduced motion, and when the film has a playable embed.
  // Everywhere else it falls back to the cover photo. The photo always renders
  // first so there's never a blank hero while the video warms up.
  const bgFilm = videos[0];
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 768px)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection?.saveData;
    setVideoBg(wide && !reduced && !saveData && !!bgFilm?.embed);
  }, [bgFilm]);

  // A photo-only or film-only gallery has just one door — send them straight in
  // rather than showing a lone card. The hero still shows first.
  const hasFilm = videos.length > 0;
  const hasPhotos = photos.length > 0;
  const onlyOne = hasFilm !== hasPhotos;

  return (
    <div className="cover">
      <section className="hero cover-hero">
        <div className="hero-media">
          {cover && (
            <img
              className={`hero-img ${heroLoaded ? 'loaded' : ''}`}
              src={cover.src || cover.thumb}
              alt=""
              onLoad={() => setHeroLoaded(true)}
            />
          )}
          {videoBg && (
            <div className="hero-video" aria-hidden>
              <iframe
                src={`${bgFilm.embed}&autoplay=true&loop=true&muted=true&preload=true`}
                title=""
                tabIndex={-1}
                allow="autoplay; encrypted-media; picture-in-picture;"
              />
            </div>
          )}
        </div>
        {/* Thin inset frame over the cover, set in from every edge by the same
            proportional margin at any width. Purely decorative — never eats clicks. */}
        <span className="hero-frame" aria-hidden />

        <div className="hero-inner">
          {/* The studio mark crowns the title block rather than sitting in a
              corner, so the cover reads as one centred composition. */}
          <div className="hero-mark">
            <StudioMark />
          </div>

          {/* Photographer's own line, defaulting to "The Wedding of". An empty
              string is a deliberate choice to show nothing at all. */}
          {gallery.hero_kicker !== '' && (
            <span className="label on-dark">{gallery.hero_kicker || 'The Wedding of'}</span>
          )}
          <h1 className="hero-name">{gallery.client_name}</h1>
          {gallery.wedding_date && <p className="hero-date">{formatDate(gallery.wedding_date)}</p>}
          {gallery.welcome_message && (
            <>
              <div className="hero-rule" />
              <p className="hero-note">{gallery.welcome_message}</p>
            </>
          )}

          <div className={`entrances ${onlyOne ? 'single' : ''}`}>
            {hasFilm && (
              <Entrance
                to={`/${slug}/film`}
                image={filmPoster}
                kicker={videos.length > 1 ? `${videos.length} Films` : 'The Film'}
                title={videos.length > 1 ? 'The Films' : 'The Film'}
              />
            )}
            {hasPhotos && (
              <Entrance
                to={`/${slug}/photos`}
                image={photoTile}
                kicker={`${photos.length} Photographs`}
                title="The Photographs"
              />
            )}
          </div>

          {/* Sends the gallery to the TV app (Android TV / Samsung) by the
              short code the TV shows - see WatchOnTv.jsx. */}
          <button className="watch-tv-link" onClick={() => setTvOpen(true)}>
            Watch on TV
          </button>
        </div>

        {onlyOne && (
          <button
            className="scroll-cue-btn"
            onClick={() => navigate(`/${slug}/${hasFilm ? 'film' : 'photos'}`)}
            aria-label="Enter gallery"
          >
            <span className="label on-dark">Enter</span>
            <span className="scroll-cue"><span /></span>
          </button>
        )}
      </section>

      {tvOpen && <WatchOnTvSheet slug={slug} onClose={() => setTvOpen(false)} />}

      {branding?.footer_text && (
        <p className="tiny cover-footer">{branding.footer_text}</p>
      )}
    </div>
  );
}

function Entrance({ to, image, kicker, title }) {
  return (
    <Link to={to} className="entrance">
      {image && <img src={image} alt="" aria-hidden />}
      <span className="entrance-veil" />
      <span className="entrance-body">
        <span className="label on-dark">{kicker}</span>
        <span className="entrance-title serif">{title}</span>
        <span className="entrance-go">
          View <IconArrowRight width={16} height={16} />
        </span>
      </span>
    </Link>
  );
}
